import React, { useState, useEffect, useRef } from 'react';
import { Post as PostData } from './HomePage';
import { API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { ProgressiveImage } from './ProgressiveImage';
import { formatRestaurantType } from '../utils/stringUtils';
import './PostModal.css';
import { DEFAULT_PROFILE_PICTURE } from '../constants/defaults';
import { List } from '../types/list';

interface PostModalProps {
  post: PostData;
  onClose: () => void;
}

const PostModal: React.FC<PostModalProps> = ({ post, onClose }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: number]: boolean }>({});
  const photos = post.photos || (post.first_photo ? [post.first_photo] : []);
  const { currentUser } = useAuth();
  const touchStartY = useRef<number | null>(null);
  const touchCurrentY = useRef<number | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const modalDetailsRef = useRef<HTMLDivElement>(null);
  const [lists, setLists] = useState<List[]>([]);
  const [isAddingToList, setIsAddingToList] = useState(false);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add effect to handle visibility
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  // Add this effect to fetch lists when the modal opens
  useEffect(() => {
    const fetchLists = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/lists`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch lists');
        const data = await response.json();
        setLists(data);
      } catch (err) {
        console.error('Error fetching lists:', err);
      }
    };

    fetchLists();
  }, [currentUser]);

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleYumClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!currentUser) {
      alert('Please log in to Yum this post!');
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_ENDPOINTS.POSTS}/${post.id}/yum`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        post.is_yummed = result.is_yummed;
        post.yum_count = result.is_yummed ? (post.yum_count || 0) + 1 : (post.yum_count || 0) - 1;
      } else {
        console.error('Failed to toggle yum status:', response.status, await response.text());
        alert('Failed to toggle yum status.');
      }
    } catch (error) {
      console.error('Error toggling yum status:', error);
      alert('An error occurred while trying to toggle yum status.');
    }
  };

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(currentPhotoIndex - 1);
    }
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(currentPhotoIndex + 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        if (currentPhotoIndex > 0) {
          setCurrentPhotoIndex(currentPhotoIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (currentPhotoIndex < photos.length - 1) {
          setCurrentPhotoIndex(currentPhotoIndex + 1);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPhotoIndex, photos.length, onClose]);

  // Effect to control body scroll
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Swipe up/down logic for mobile
  useEffect(() => {
    if (!isMobile) return;

    const modalElement = modalContentRef.current;
    const detailsElement = modalDetailsRef.current;
    if (!modalElement || !detailsElement) return;

    let elementToTranslate: HTMLElement | null = null;
    let maxDragDistance = 0;
    const DRAG_HANDLE_THRESHOLD = 60; // Pixels from the top edge to allow dragging

    const handleTouchStart = (e: TouchEvent) => {
      const targetElement = e.target as HTMLElement;
      const isImageContainerTarget = targetElement.closest('.modal-image-container');
      const isDetailsContainerTarget = targetElement.closest('.modal-details');
      
      // Ensure detailsElement exists before proceeding
      if (!detailsElement) return;

      const detailsScrolledToTop = detailsElement.scrollTop === 0;

      touchStartY.current = e.touches[0].clientY;
      touchCurrentY.current = touchStartY.current;
      elementToTranslate = null;

      maxDragDistance = window.innerHeight * 0.5;

      if (!isDetailsVisible && isImageContainerTarget) {
        // Swiping up on image to reveal details OR down to close modal
        elementToTranslate = detailsElement;
        detailsElement.style.transition = 'none';
        detailsElement.classList.add('dragging');
      } else if (isDetailsVisible && isDetailsContainerTarget && detailsScrolledToTop) {
        // Check if touch starts near the top edge when scrolled to top
        const detailsTopEdge = detailsElement.getBoundingClientRect().top;
        if (touchStartY.current < detailsTopEdge + DRAG_HANDLE_THRESHOLD) {
          // Swiping down starting *near the top* when details are visible and scrolled to top
          elementToTranslate = detailsElement;
          detailsElement.style.transition = 'none';
        } else {
          // Touch started lower down, allow native scroll (do nothing here)
          touchStartY.current = null;
          touchCurrentY.current = null;
        }
      } else if (!isDetailsVisible && isDetailsContainerTarget) {
        // Handles closing swipe if touch starts on hidden pane (less likely)
        elementToTranslate = modalElement;
        modalElement.style.transition = 'none';
      } else {
        // Catch-all: prevent drag/swipe, allow native behavior
        touchStartY.current = null;
        touchCurrentY.current = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === null || touchCurrentY.current === null || !elementToTranslate) return;

      touchCurrentY.current = e.touches[0].clientY;
      const deltaY = touchCurrentY.current - touchStartY.current;

      if (elementToTranslate === detailsElement) {
        if (!isDetailsVisible) {
          if (deltaY < 0) { // Swiping UP to reveal details
            const dragDistance = Math.min(Math.abs(deltaY), maxDragDistance);
            // CSS var controls transform: translateY(calc(100% - var(--drag-distance)))
            detailsElement.style.setProperty('--drag-distance', `${dragDistance}px`);
          } else if (deltaY > 0) { // Swiping DOWN to close modal
            // Switch to modal element for downward swipe
            elementToTranslate = modalElement;
            modalElement.style.transition = 'none';
            modalElement.style.transform = `translateY(${Math.max(0, deltaY)}px)`;
          }
        } else { // isDetailsVisible is true
          if (deltaY > 0 && detailsElement.scrollTop === 0) { // Swiping DOWN to hide details
            const dragDistance = Math.min(deltaY, maxDragDistance);
            // Manually set transform from 50vh downwards
            detailsElement.style.transform = `translateY(calc(50vh + ${dragDistance}px))`;
            detailsElement.classList.remove('dragging'); // Ensure dragging class is off for this swipe direction
          }
        }
      } else if (elementToTranslate === modalElement) {
        // Swiping DOWN to close the whole modal
        if (deltaY > 0) {
          modalElement.style.transform = `translateY(${Math.max(0, deltaY)}px)`;
        }
      }
    };

    const handleTouchEnd = () => {
      if (touchStartY.current === null || touchCurrentY.current === null || !elementToTranslate) {
        // Reset styles if touch ended unexpectedly
        if (modalElement) modalElement.style.transition = '';
        if (detailsElement) {
          detailsElement.style.transition = '';
          detailsElement.classList.remove('dragging');
          detailsElement.style.removeProperty('--drag-distance');
        }
        return;
      }

      const deltaY = touchCurrentY.current - touchStartY.current;
      const threshold = 80; // Keep the threshold

      // Reset transitions
      modalElement.style.transition = '';
      detailsElement.style.transition = '';
      detailsElement.classList.remove('dragging');
      detailsElement.style.removeProperty('--drag-distance');

      if (elementToTranslate === detailsElement) {
        detailsElement.style.transform = ''; // Clear inline transform set during drag down
        if (!isDetailsVisible && deltaY < -threshold) {
          // Snap open to 50vh
          setIsDetailsVisible(true);
        } else if (isDetailsVisible && deltaY > threshold) {
          // Snap closed (back to 100%)
          setIsDetailsVisible(false);
        } else {
          // Snap back to original position (either 50vh or 100%)
          // No state change needed, CSS transition handles snap back
        }
      } else if (elementToTranslate === modalElement) {
        modalElement.style.transform = ''; // Clear inline transform
        if (!isDetailsVisible && deltaY > threshold) {
          // Close the modal
          handleClose();
        }
      }

      // Reset touch state
      touchStartY.current = null;
      touchCurrentY.current = null;
      elementToTranslate = null;
    };

    modalElement.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    modalElement.addEventListener('touchmove', handleTouchMove, { passive: false }); // passive: false needed for preventDefault potentially
    modalElement.addEventListener('touchend', handleTouchEnd);
    modalElement.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      modalElement.removeEventListener('touchstart', handleTouchStart, { capture: true });
      modalElement.removeEventListener('touchmove', handleTouchMove);
      modalElement.removeEventListener('touchend', handleTouchEnd);
      modalElement.removeEventListener('touchcancel', handleTouchEnd);
      if (modalElement) {
        modalElement.style.transform = '';
        modalElement.style.transition = '';
      }
      if (detailsElement) {
        detailsElement.style.transform = '';
        detailsElement.style.transition = '';
        detailsElement.classList.remove('dragging');
        detailsElement.style.removeProperty('--drag-distance');
      }
    };
  }, [onClose, isDetailsVisible, isMobile]);

  const currentPhoto = photos.length > 0 ? photos[currentPhotoIndex] : null;

  const toggleDescription = (index: number) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAddToList = async () => {
    if (!currentUser || !selectedListId) {
      console.log('Missing required data:', { currentUser, selectedListId });
      return;
    }
    setIsAddingToList(true);
    setListError(null);

    try {
      const token = await currentUser.getIdToken();
      const requestBody = {
        itemId: post.id.toString(), // Ensure itemId is a string
        type: 'post' as const,
        blurb: post.title // Optional: use post title as blurb
      };
      
      console.log('Making API request to:', `${API_BASE_URL}/api/lists/${selectedListId}/items`);
      console.log('Request body:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/api/lists/${selectedListId}/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('API Response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      if (response.status === 204) {
        // Success - update local state
        setLists(prevLists => prevLists.map(list => {
          if (list.id === selectedListId) {
            // Ensure metadata exists and is properly structured
            const currentMetadata = list.metadata || { items: [] };
            const currentItems = Array.isArray(currentMetadata.items) ? currentMetadata.items : [];
            
            return {
              ...list,
              metadata: {
                ...currentMetadata,
                items: [
                  ...currentItems,
                  { id: post.id.toString(), type: 'post' }
                ]
              },
            };
          }
          return list;
        }));
        setSelectedListId(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add post to list. Please try again.';
      setListError(errorMessage);
      console.error('Error adding post to list:', err);
    } finally {
      setIsAddingToList(false);
    }
  };

  const renderImageSection = () => (
    <div className="modal-image-container">
      {currentPhoto && (
        <>
          {isMobile && (
            <div className="mobile-header">
              <img
                src={post.user_avatar_url || DEFAULT_PROFILE_PICTURE}
                alt={post.user_display_name || 'User'}
                className="modal-user-avatar"
              />
              <Link 
                to={post.user_id === currentUser?.uid ? '/profile' : `/profile/u/${post.user_name}`}
                className="modal-user-name"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                {post.user_display_name}
              </Link>
            </div>
          )}

          <ProgressiveImage
            src={`${API_BASE_URL}/api/posts/images/${currentPhoto.displayUrl}`}
            thumbnailSrc={`${API_BASE_URL}/api/posts/images/${currentPhoto.thumbnailUrl}`}
            alt={currentPhoto.name || post.title || 'Post image'}
            className="modal-image"
            objectFit="contain"
          />

          {isMobile && <div className="swipe-up-indicator"></div>}

          {(currentPhoto.name || currentPhoto.description) && (
            <div className="photo-info-overlay">
              <div className="photo-text-content">
                {currentPhoto.name && (
                  <div className="photo-name-overlay">{currentPhoto.name}</div>
                )}
                {currentPhoto.description && (
                  <>
                    <div 
                      className={`photo-description-overlay ${expandedDescriptions[currentPhotoIndex] ? 'expanded' : ''}`}
                      onClick={() => {
                        // Only toggle if the description is actually truncated (show more button exists)
                        if (currentPhoto.description && currentPhoto.description.length > 150) {
                          toggleDescription(currentPhotoIndex);
                        }
                      }}
                      style={currentPhoto.description && currentPhoto.description.length > 150 ? { cursor: 'pointer' } : {}}
                    >
                      {currentPhoto.description}
                    </div>
                    {currentPhoto.description.length > 150 && (
                      <button 
                        className="show-more-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the div's onClick
                          toggleDescription(currentPhotoIndex);
                        }}
                      >
                        {expandedDescriptions[currentPhotoIndex] ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {photos.length > 1 && (
            <>
              <button 
                className="image-nav-button prev" 
                onClick={handlePrevPhoto}
                disabled={currentPhotoIndex === 0}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              <button 
                className="image-nav-button next" 
                onClick={handleNextPhoto}
                disabled={currentPhotoIndex === photos.length - 1}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              <div className="image-counter">
                {currentPhotoIndex + 1} / {photos.length}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  const renderDetailsSection = () => (
    <div ref={modalDetailsRef} className="modal-details">
      {isMobile && <div className="swipe-down-indicator"></div>}
      
      {!isMobile && (
        <div className="modal-author">
          <img
            src={post.user_avatar_url || DEFAULT_PROFILE_PICTURE}
            alt={post.user_display_name || 'User'}
            className="modal-author-avatar"
          />
          <Link 
            to={post.user_id === currentUser?.uid ? '/profile' : `/profile/u/${post.user_name}`}
            className="modal-author-name"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            {post.user_display_name}
          </Link>
          <span className="modal-post-time">
            {new Date(post.visit_date || post.created_at).toLocaleDateString()}
          </span>
        </div>
      )}
      
      <div className="modal-body">
        <h2 className="modal-title">{post.title}</h2>
        <p className="modal-description">{post.content}</p>
        
        {post.restaurant_name && (
          <div className="modal-restaurant-info">
            <div className="modal-restaurant-name">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="modal-location-icon">
                <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.757.433.36.36 0 00.28.14l.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clipRule="evenodd" />
              </svg>
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(post.restaurant_name + ' ' + post.restaurant_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="restaurant-info-link"
                onClick={(e) => e.stopPropagation()}
              >
                <span>{post.restaurant_name}</span>
              </a>
            </div>
            {post.restaurant_address && (
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(post.restaurant_name + ' ' + post.restaurant_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="restaurant-info-link"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-restaurant-address">{post.restaurant_address}</div>
              </a>
            )}
            {post.restaurant_primary_type && (
              <div className="modal-restaurant-cuisine">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="cuisine-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                  <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" />
                  <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 6c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                </svg>
                <span>{formatRestaurantType(post.restaurant_primary_type)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="modal-actions">
        <button 
          className={`modal-action-button modal-yum-button ${post.is_yummed ? "text-red-500" : ""}`}
          onClick={handleYumClick}
          disabled={!currentUser || currentUser.uid === post.user_id}
          title={
            !currentUser 
              ? "Log in to Yum" 
              : currentUser.uid === post.user_id 
              ? "You cannot Yum your own post" 
              : "Yum this post!"
          }
        >
          <svg className={`w-5 h-5 ${post.is_yummed ? "text-red-500" : ""}`} fill={post.is_yummed ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span>Yum! ({post.yum_count !== undefined ? post.yum_count : 0})</span>
        </button>

        {currentUser && (
          <div className="add-to-list-container">
            <select
              className="list-select"
              value={selectedListId || ''}
              onChange={(e) => setSelectedListId(e.target.value ? Number(e.target.value) : null)}
              disabled={isAddingToList}
            >
              <option value="">Add to list...</option>
              {lists.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
            {selectedListId && (
              <button
                className="add-to-list-button"
                onClick={handleAddToList}
                disabled={isAddingToList}
              >
                {isAddingToList ? 'Adding...' : 'Add'}
              </button>
            )}
            {listError && <div className="list-error">{listError}</div>}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`modal-overlay ${isVisible ? 'visible' : ''}`} onClick={handleClose}>
      <div
        ref={modalContentRef}
        className={`post-modal-content ${isDetailsVisible ? 'details-visible' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
          </svg>
        </button>
        
        {renderImageSection()}
        {renderDetailsSection()}
      </div>
    </div>
  );
};

export default PostModal; 